/**
 * Locales enabled for SEO. The source of truth is the seo-db `locales` table;
 * this list is only used as a fallback when seo-db is unavailable and as the
 * default redux state.
 */
export const DEFAULT_SEO_ACTIVE_LOCALES = [
  'en',
  'en-GB',
  'es',
  'de',
  'fr',
  'no',
  'da',
  'sv',
  'pt',
  'pt-BR'
]

/**
 * Locales deactivated for SEO but still served. They keep their filter
 * attributes so their filtered SRPs resolve instead of redirecting to the
 * unfiltered page.
 *
 * It should be cleaned up in this story sc-196559
 */
export const SUNSET_SEO_LOCALES = ['it', 'no', 'pt', 'pt-BR', 'sv']

/**
 * English is served under additional hreflang labels that all resolve to the
 * `/en` path: regional `en-US` and the `x-default` fallback. Every other locale
 * maps 1:1 to its own path.
 */
const ENGLISH_LOCALE = 'en'
const ENGLISH_HREFLANG_ALIASES = ['en-US']
const X_DEFAULT_HREFLANG = 'x-default'

/**
 * Builds a map from `hreflang` attribute to locale code out of the list of
 * active SEO locales. The keys are the hreflang attributes to emit, the values
 * are the locale codes used to build each alternate URL.
 *
 * For example, `['en', 'es']` yields hreflang attributes `en`, `en-US` and
 * `x-default` (all resolving to the `en` locale) plus `es` resolving to `es`.
 */
export const buildHreflangToLocaleCode = (
  activeLocales: string[]
): Record<string, string> => {
  const map: Record<string, string> = {}

  for (const locale of activeLocales) {
    map[locale] = locale

    if (locale === ENGLISH_LOCALE) {
      for (const alias of ENGLISH_HREFLANG_ALIASES) {
        map[alias] = ENGLISH_LOCALE
      }
      map[X_DEFAULT_HREFLANG] = ENGLISH_LOCALE
    }
  }

  return map
}
